import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "@/lib/sentry";

interface State {
  hasError: boolean;
  error: Error | null;
  showStack: boolean;
  copied: boolean;
}

class GlobalErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, error: null, showStack: false, copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[GlobalErrorBoundary]", error, info.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
    });
  }

  buildDiagnostics() {
    const { error } = this.state;
    let userId: string | undefined;
    try {
      const raw = localStorage.getItem(
        Object.keys(localStorage).find((k) => k.startsWith("sb-") && k.endsWith("-auth-token")) || ""
      );
      if (raw) userId = JSON.parse(raw)?.user?.id;
    } catch {}
    return JSON.stringify(
      {
        url: window.location.href,
        userId: userId || null,
        error: error ? `${error.name}: ${error.message}` : null,
        stack: error?.stack || null,
        lastFatal: (window as any).__lastFatalError || null,
        timestamp: new Date().toISOString(),
      },
      null,
      2
    );
  }

  copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(this.buildDiagnostics());
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = this.buildDiagnostics();
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, showStack, copied } = this.state;

    const containerStyle: React.CSSProperties = {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0f172a",
      color: "#e2e8f0",
      fontFamily: "system-ui, -apple-system, sans-serif",
      padding: "24px",
    };
    const cardStyle: React.CSSProperties = {
      maxWidth: 520,
      width: "100%",
      background: "#1e293b",
      borderRadius: 12,
      padding: "32px 28px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    };
    const btnBase: React.CSSProperties = {
      padding: "10px 20px",
      borderRadius: 8,
      border: "none",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 14,
    };

    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 16 }}>
            The app encountered an unexpected error. Your data is safe.
          </p>
          <div
            style={{
              background: "#0f172a",
              borderRadius: 8,
              padding: "12px 16px",
              fontSize: 13,
              fontFamily: "monospace",
              color: "#f87171",
              marginBottom: 16,
              wordBreak: "break-word",
            }}
          >
            {error?.message || "Unknown error"}
          </div>

          {showStack && error?.stack && (
            <pre
              style={{
                background: "#0f172a",
                borderRadius: 8,
                padding: "12px 16px",
                fontSize: 11,
                fontFamily: "monospace",
                color: "#94a3b8",
                marginBottom: 16,
                overflow: "auto",
                maxHeight: 200,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {error.stack}
            </pre>
          )}

          <button
            onClick={() => this.setState((s) => ({ showStack: !s.showStack }))}
            style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 13, marginBottom: 20, display: "block" }}
          >
            {showStack ? "Hide details" : "Show details"}
          </button>

          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={() => window.location.reload()}
              style={{ ...btnBase, background: "#3b82f6", color: "#fff", flex: 1 }}
            >
              Reload
            </button>
            <button
              onClick={this.copyDiagnostics}
              style={{ ...btnBase, background: "#334155", color: "#e2e8f0", flex: 1 }}
            >
              {copied ? "Copied!" : "Copy diagnostics"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default GlobalErrorBoundary;
